import {
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { routeLabel } from "../../lib/trip-route";
import { partyLabel, partyOf } from "../../lib/trip-party";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import { TripCardMenu } from "./trip-card-menu";
import { TripShareMenu } from "./trip-share-menu";
import { thumbSrcSet, thumbUrl } from "../map/thumb-url";
import { formatNumber } from "../../internal/format";
import { useTripActions } from "../../state/trip-context";
import { formatTripDates } from "./trip-date";
import type { DiscoverItem, Trip } from "../../types";

import { Icon } from "@majourneys/icons";

/** How many stop photographs make up the face of a card. */
const COVER_SLOTS = 3;

function stopTotal(trip: Trip): number {
  return trip.days.reduce((n, d) => n + d.stops.length, 0);
}

/**
 * Up to three cover slots, in itinerary order — one per stop, `null` where the
 * stop's venue has no photograph. Slots track stops rather than photographs so
 * the face of a card can never disagree with the "3 spots" underneath it: a
 * stop with no image leaves a blank tile, not a missing one.
 *
 * The catalogue wins, and the snapshot written when the stop was saved is the
 * fallback — a slow or failed load leaves the card looking like the trip it is
 * rather than a row of blanks.
 */
function coverSlots(
  trip: Trip,
  resolveItem: (id: string) => DiscoverItem | null,
): (string | null)[] {
  const out: (string | null)[] = [];
  for (const day of trip.days) {
    for (const stop of day.stops) {
      out.push(resolveItem(stop.itemId)?.image || stop.snapshot?.image || null);
      if (out.length === COVER_SLOTS) return out;
    }
  }
  return out;
}


/** The face of a card: up to three stop photographs, or a line saying there are none. */
function TripCardFace({
  covers,
  stops,
  t,
}: {
  trip: Trip;
  covers: (string | null)[];
  stops: number;
  t: (key: string) => string;
}) {
  return (
    <span className={`mj-trip-card__plate${stops === 0 ? " is-empty" : ""}`}>
      {stops === 0 ? (
        <span className="mj-trip-card__plate-empty">{t("trip.card_no_stops")}</span>
      ) : (
        covers.map((src, i) =>
          src ? (
            <img
              key={`${src}-${i}`}
              className="mj-trip-card__cover"
              src={thumbUrl(src, 320)}
              srcSet={thumbSrcSet(src, [160, 320, 480])}
              sizes="120px"
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span key={`blank-${i}`} className="mj-trip-card__cover mj-trip-card__cover--blank" />
          ),
        )
      )}
    </span>
  );
}

interface MobileTripListProps {
  readonly trips: readonly Trip[];
  readonly resolveItem: (id: string) => DiscoverItem | null;
  readonly cityNameOf: (slug: string) => string;
  readonly lang: string;
  readonly onOpen: (tripId: string) => void;
  readonly onCreate: () => void;
}

/**
 * Trips opens on the set, not on one trip.
 *
 * The phone surface used to open straight into the active trip with a dropdown
 * in the sheet header to switch — so every other trip only existed if you
 * remembered it did, and the dropdown was the only thing on screen that said
 * how many you had. The section now opens on the whole set: a card per trip,
 * recognised by the photographs of its first stops rather than by its name,
 * and a trip opens over the list. Switching is tapping the one you want.
 *
 * A trip with no stops yet says so in words instead of showing three grey
 * rectangles, and rename / delete / share sit on the card, which is where you
 * are when you want them — none of them had a home on the phone before.
 *
 * The dropdown's keyboard behaviour did not leave with it. Arrows walk the
 * cards, Home and End go to the ends, and each card's menu traps and returns
 * focus the way the dropdown did.
 */
export function MobileTripList({
  trips,
  resolveItem,
  cityNameOf,
  lang,
  onOpen,
  onCreate,
}: MobileTripListProps) {
  const { t, i18n } = useTranslation();
  const { renameTrip, deleteTrip } = useTripActions();
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Which card has swapped its name for a field. */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  // Escape has to be able to beat the blur that follows it: unmounting the
  // field fires `blur`, which would otherwise commit the very edit the
  // traveller just abandoned.
  const cancelledRef = useRef(false);

  const cardButtons = () =>
    listRef.current
      ? Array.from(
          listRef.current.querySelectorAll<HTMLButtonElement>("[data-trip-open]"),
        )
      : [];

  const onListKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Only from a card. A card's own menu owns its arrows, and a rename field
    // owns everything typed into it — neither should move the selection.
    const target = e.target as HTMLElement | null;
    if (target?.hasAttribute("data-trip-open") !== true) return;
    const buttons = cardButtons();
    if (buttons.length === 0) return;
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const focusAt = (index: number) => {
      e.preventDefault();
      buttons[index]?.focus();
    };
    switch (e.key) {
      case "ArrowDown":
        focusAt(i < 0 ? 0 : (i + 1) % buttons.length);
        break;
      case "ArrowUp":
        focusAt(i < 0 ? buttons.length - 1 : (i - 1 + buttons.length) % buttons.length);
        break;
      case "Home":
        focusAt(0);
        break;
      case "End":
        focusAt(buttons.length - 1);
        break;
      default:
        break;
    }
  };

  const startRename = (trip: Trip) => {
    cancelledRef.current = false;
    setDraft(trip.name);
    setRenamingId(trip.id);
  };

  const commitRename = (trip: Trip) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      setRenamingId(null);
      return;
    }
    const trimmed = draft.trim();
    if (trimmed && trimmed !== trip.name) renameTrip(trip.id, trimmed);
    setRenamingId(null);
  };

  const confirmDelete = (trip: Trip): boolean => {
    if (confirm(t("trip.confirm_delete", { name: trip.name })) !== true) {
      return false;
    }
    deleteTrip(trip.id);
    return true;
  };

  return (
    <div className="mj-trip-mobile-list">
      <header className="mj-trip-mobile-list__head">
        <div className="mj-trip-mobile-list__headrow">
          <h2>{t("trip.your_trips")}</h2>
          <button
            type="button"
            className="mj-trip-mobile-list__new"
            onClick={onCreate}
          >
            <Icon name="ri-add-line" aria-hidden="true" />
            {t("trip.new_trip")}
          </button>
        </div>
        <p>{t("trip.your_trips_subtitle")}</p>
      </header>

      <div
        className="mj-trip-mobile-list__items"
        ref={listRef}
        role="list"
        aria-label={t("trip.your_trips")}
        onKeyDown={onListKeyDown}
      >
        {trips.map((trip) => {
          const stops = stopTotal(trip);
          const covers = coverSlots(trip, resolveItem);
          const renaming = renamingId === trip.id;
          const meta = [
            routeLabel(trip, cityNameOf, i18n.dir(lang) === "rtl"),
            formatTripDates(trip, lang, (start, end) => t("trip.when.range", { start, end })),
            t("trip.day_count", { count: trip.days.length }),
            // Who is going, as the app's card says it.
            partyLabel(t, partyOf(trip, DEFAULT_TRIP_PEOPLE)),
            t("trip.timeline.spots", {
              count: stops,
              formattedCount: formatNumber(stops, lang),
            }),
          ]
            .filter((part): part is string => Boolean(part))
            // Each part stays on one line ("0 stops" never splits); the line
            // breaks between parts. The route (first) may break between cities.
            .map((part, i) => (i === 0 ? part : part.replace(/ /g, "\u00A0")))
            .join(" · ");
          return (
            <article
              key={trip.id}
              role="listitem"
              className="mj-trip-card mj-dk-frame"
            >
              {/* The whole card opens the trip: photographs, name and the line
                  under it are one target. Rename and share sit on top as their
                  own buttons, so pressing them never opens the trip by mistake.
                  While renaming, the card stops being a button: a text field
                  cannot live inside one. */}
              {renaming ? (
                <div className="mj-trip-card__open is-renaming">
                  <TripCardFace trip={trip} covers={covers} stops={stops} t={t} />
                  <span className="mj-trip-card__row">
                    <input
                      type="text"
                      className="mj-trip-card__rename"
                      autoFocus
                      maxLength={60}
                      value={draft}
                      aria-label={t("trip.trip_name")}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => commitRename(trip)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelledRef.current = true;
                          e.currentTarget.blur();
                        }
                      }}
                    />
                  </span>
                  <span className="mj-trip-card__meta">{meta}</span>
                </div>
              ) : (
                <button
                  type="button"
                  data-trip-open
                  className="mj-trip-card__open"
                  onClick={() => onOpen(trip.id)}
                  aria-label={t("trip.open_named", { name: trip.name })}
                  aria-describedby={`mj-trip-card-meta-${trip.id}`}
                >
                  <TripCardFace trip={trip} covers={covers} stops={stops} t={t} />
                  <span className="mj-trip-card__row">
                    <span className="mj-trip-card__name">{trip.name}</span>
                  </span>
                  <span className="mj-trip-card__meta" id={`mj-trip-card-meta-${trip.id}`}>
                    {meta}
                  </span>
                </button>
              )}

              <div className="mj-trip-card__tools">
                <TripShareMenu trip={trip} resolveItem={resolveItem} />
                <TripCardMenu
                  tripName={trip.name}
                  onRename={() => startRename(trip)}
                  onDelete={() => confirmDelete(trip)}
                />
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
